import pick from 'lodash/pick';

import {getClauseTrees, getLogicalOperators} from 'common/query-builder/cohorts-query-builder/util/expression-tree';

import {LogicalOperator} from '../enums/expression-enums';
import {OperatorNode} from '../expression/operator-node';
import {ClauseOperators} from '../types/builder-state-types';
import {SerializedPeopleLiteral} from '../types/serialized-staged-cohort-types';
import {PeoplePropertyClause} from './people-property-clause';
import {Literal} from './literal';

interface PeopleLiteralOptions {
  clauses: PeoplePropertyClause[];
  labelPrefix: string;
  clauseOperators?: ClauseOperators;
}

export class PeopleLiteral extends Literal {
  public static deserialize(options: SerializedPeopleLiteral): PeopleLiteral {
    return new PeopleLiteral({
      clauseOperators: options.clauseOperators || null,
      clauses: options.clauses.map((c) => PeoplePropertyClause.deserialize(c)),
      labelPrefix: options.labelPrefix,
    });
  }

  public static createDefaultWithPrefix(labelPrefix: string): PeopleLiteral {
    return new PeopleLiteral({
      clauseOperators: null,
      clauses: [PeoplePropertyClause.createDefault()],
      labelPrefix,
    });
  }

  public static createFromParts({tree, labelPrefix}: {tree: OperatorNode; labelPrefix: string}): PeopleLiteral {
    let clauseTrees = getClauseTrees(tree);
    if (!clauseTrees.length) {
      // cohorts not built by builder, but renderable in UI
      clauseTrees = [tree];
    }

    const clauses = clauseTrees.map((t) => PeoplePropertyClause.createFromParts({tree: t}));
    let clauseOperators: ClauseOperators = null;
    if (clauses.length > 1) {
      const operators = getLogicalOperators(tree);
      clauseOperators = [operators[0], operators[1] || LogicalOperator.And];
    }

    return new PeopleLiteral({
      clauseOperators,
      clauses,
      labelPrefix,
    });
  }

  public declare readonly clauses: PeoplePropertyClause[];

  constructor(options: PeopleLiteralOptions) {
    super(options);
    this.clauses = options.clauses;
  }

  get properties(): string[] {
    return this.clauses.map((clause) =>
      Array.isArray(clause.propertyFilter.property)
        ? clause.propertyFilter.property.join(`.`)
        : clause.propertyFilter.property,
    );
  }

  public serialize(): SerializedPeopleLiteral {
    return {
      clauseOperators: this.clauseOperators,
      clauses: this.clauses.map((c) => c.serialize()),
      labelPrefix: this.labelPrefix,
    };
  }

  public addDefaultClause(): PeopleLiteral {
    return this.addClause<PeopleLiteral>(PeoplePropertyClause.createDefault());
  }

  public setClauseNegated(negated: boolean, clauseIdx: number): PeopleLiteral {
    return this.replaceClause<PeopleLiteral>(this.clauses[clauseIdx].setNegated(negated), clauseIdx);
  }

  public setClauseOperators(clauseOperators: ClauseOperators): PeopleLiteral {
    return this.newLiteral({clauseOperators});
  }

  public toTrackingData(): Obj {
    const hasClauseOperators = !!this.clauseOperators;
    return {
      'initial operator': hasClauseOperators ? this.clauseOperators[0] : null,
      'is people literal': true,
      'join operator': hasClauseOperators ? this.clauseOperators[1] : null,
      'literal clauses': this.clauses.length,
    };
  }

  public sanitizeForDataGroup({dataGroupId}): Literal {
    let newLiteralOptions: Partial<PeopleLiteralOptions> = {
      clauses: this.clauses.filter((clause) => !clause.isInvalidForDataGroup({dataGroupId})),
    };
    if (newLiteralOptions.clauses.length === 1) {
      newLiteralOptions = {...newLiteralOptions, ...{clauseOperators: null}};
    }

    return this.newLiteral(newLiteralOptions);
  }

  protected getOperatorLabelAtIndex(idx: number): string {
    const operatorIdx = Math.min(idx - 1, 1);
    return this.clauseOperators[operatorIdx];
  }

  // @override
  protected newLiteral(options: Partial<PeopleLiteralOptions>): PeopleLiteral {
    const oldOptions: PeopleLiteralOptions = pick(this, [`clauseOperators`, `clauses`, `labelPrefix`]);
    return new PeopleLiteral({...oldOptions, ...options});
  }
}
